import {
  useState,
  useRef,
  useEffect,
  useCallback,
  useImperativeHandle,
  forwardRef,
} from "react"

const CHECKERBOARD_BG = `url("data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAABAAAAAQBAMAAADt3eJSAAAAAXNSR0IArs4c6QAAAARnQU1BAACxjwv8YQUAAAAGUExURb+/v////5nD/3QAAAAJcEhZcwAADsMAAA7DAcdvqGQAAAAUSURBVBjTYwABQSCglEENMxgYGAAynwRB8BEAgQAAAABJRU5ErkJggg==")`

const MAX_UNDO_STACK = 20

export interface EraserCanvasRef {
  undo: () => void
  canUndo: boolean
}

interface EraserCanvasProps {
  imageSource: string
  brushSize: number
  onUpdate: (blob: Blob) => void
  onCanUndoChange?: (canUndo: boolean) => void
}

export const EraserCanvas = forwardRef<EraserCanvasRef, EraserCanvasProps>(
  function EraserCanvas({ imageSource, brushSize, onUpdate, onCanUndoChange }, ref) {
    const canvasRef = useRef<HTMLCanvasElement>(null)
    const wrapperRef = useRef<HTMLDivElement>(null)
    const cursorRef = useRef<HTMLDivElement>(null)
    const undoStackRef = useRef<ImageData[]>([])
    const isDrawingRef = useRef(false)
    const lastPointRef = useRef<{ x: number; y: number } | null>(null)
    const [canUndo, setCanUndo] = useState(false)
    const [cursorPos, setCursorPos] = useState<{ x: number; y: number } | null>(
      null
    )

    useImperativeHandle(
      ref,
      () => ({
        undo: handleUndo,
        canUndo,
      }),
      [canUndo]
    )

    useEffect(() => {
      onCanUndoChange?.(canUndo)
    }, [canUndo, onCanUndoChange])

    // Load image onto canvas
    useEffect(() => {
      const canvas = canvasRef.current
      if (!canvas) return

      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const img = new Image()
      img.onload = () => {
        canvas.width = img.width
        canvas.height = img.height
        ctx.clearRect(0, 0, canvas.width, canvas.height)
        ctx.globalCompositeOperation = "source-over"
        ctx.drawImage(img, 0, 0)
        undoStackRef.current = []
        setCanUndo(false)
      }
      img.src = imageSource
    }, [imageSource])

    const getScale = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return 1
      const rect = canvas.getBoundingClientRect()
      return canvas.width / rect.width
    }, [])

    const getCanvasPoint = useCallback(
      (clientX: number, clientY: number) => {
        const canvas = canvasRef.current
        if (!canvas) return { x: 0, y: 0 }
        const rect = canvas.getBoundingClientRect()
        const scale = getScale()
        return {
          x: (clientX - rect.left) * scale,
          y: (clientY - rect.top) * scale,
        }
      },
      [getScale]
    )

    const saveSnapshot = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const snapshot = ctx.getImageData(0, 0, canvas.width, canvas.height)
      const stack = [...undoStackRef.current, snapshot]
      if (stack.length > MAX_UNDO_STACK) {
        stack.shift()
      }
      undoStackRef.current = stack
      setCanUndo(true)
    }, [])

    const emitBlob = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      canvas.toBlob((blob) => {
        if (blob) onUpdate(blob)
      }, "image/png")
    }, [onUpdate])

    const handlePointerDown = useCallback(
      (e: React.PointerEvent) => {
        e.preventDefault()
        const canvas = canvasRef.current
        if (!canvas) return

        canvas.setPointerCapture(e.pointerId)
        isDrawingRef.current = true

        saveSnapshot()

        const point = getCanvasPoint(e.clientX, e.clientY)
        lastPointRef.current = point

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        const scale = getScale()
        ctx.globalCompositeOperation = "destination-out"
        ctx.lineCap = "round"
        ctx.lineJoin = "round"
        ctx.lineWidth = brushSize * scale

        // Draw a dot for single clicks
        ctx.beginPath()
        ctx.arc(point.x, point.y, (brushSize * scale) / 2, 0, Math.PI * 2)
        ctx.fill()
      },
      [brushSize, getCanvasPoint, getScale, saveSnapshot]
    )

    const handlePointerMove = useCallback(
      (e: React.PointerEvent) => {
        const canvas = canvasRef.current
        if (!canvas) return

        // Update cursor position
        const rect = canvas.getBoundingClientRect()
        setCursorPos({
          x: e.clientX - rect.left,
          y: e.clientY - rect.top,
        })

        if (!isDrawingRef.current || !lastPointRef.current) return

        const ctx = canvas.getContext("2d")
        if (!ctx) return

        const point = getCanvasPoint(e.clientX, e.clientY)
        const scale = getScale()

        ctx.globalCompositeOperation = "destination-out"
        ctx.lineCap = "round"
        ctx.lineJoin = "round"
        ctx.lineWidth = brushSize * scale

        ctx.beginPath()
        ctx.moveTo(lastPointRef.current.x, lastPointRef.current.y)
        ctx.lineTo(point.x, point.y)
        ctx.stroke()

        lastPointRef.current = point
      },
      [brushSize, getCanvasPoint, getScale]
    )

    const handlePointerUp = useCallback(
      (e: React.PointerEvent) => {
        const canvas = canvasRef.current
        if (!canvas) return

        canvas.releasePointerCapture(e.pointerId)
        if (isDrawingRef.current) {
          isDrawingRef.current = false
          lastPointRef.current = null

          const ctx = canvas.getContext("2d")
          if (ctx) {
            ctx.globalCompositeOperation = "source-over"
          }

          emitBlob()
        }
      },
      [emitBlob]
    )

    const handlePointerLeave = useCallback(() => {
      setCursorPos(null)
    }, [])

    const handleUndo = useCallback(() => {
      const canvas = canvasRef.current
      if (!canvas) return
      const ctx = canvas.getContext("2d")
      if (!ctx) return

      const stack = undoStackRef.current
      if (stack.length === 0) return

      const previous = stack[stack.length - 1]
      const newStack = stack.slice(0, -1)
      undoStackRef.current = newStack
      setCanUndo(newStack.length > 0)

      ctx.globalCompositeOperation = "source-over"
      ctx.putImageData(previous, 0, 0)
      emitBlob()
    }, [emitBlob])

    return (
      <div
        ref={wrapperRef}
        className="relative overflow-hidden rounded-lg border"
        style={{
          background: CHECKERBOARD_BG,
          backgroundRepeat: "repeat",
        }}
      >
        <canvas
          ref={canvasRef}
          className="w-full cursor-none"
          style={{ touchAction: "none" }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerLeave={handlePointerLeave}
        />

        {cursorPos && (
          <div
            ref={cursorRef}
            className="pointer-events-none absolute rounded-full border-2 border-white"
            style={{
              width: brushSize,
              height: brushSize,
              left: cursorPos.x - brushSize / 2,
              top: cursorPos.y - brushSize / 2,
              boxShadow: "0 0 0 1px rgba(0,0,0,0.5)",
            }}
          />
        )}
      </div>
    )
  }
)
